import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';

import { Plus } from '../icons';
import { useFileDropzone } from '../../hooks/useFileDropzone';
import { selectFiles } from '../../core/services/file.service';

export type ProfilePictureProps = {
  onChange?: (file: File) => void;
  onMultipleChange?: (files: File[]) => void;
  buttonName?: string;
  placeholderTitle?: string;
  placeholderDescription?: string;
  accept?: string;
  multiple?: boolean;
};

export const PhotoUploader: React.FC<ProfilePictureProps> = ({
  onChange,
  onMultipleChange,
  buttonName = 'Add Photo',
  placeholderTitle = 'Drag and drop',
  placeholderDescription = 'JPG, PNG file size no more that 3MB',
  accept = 'image/*',
  multiple,
}) => {
  const [_, setUrl] = useState<string | undefined>();

  const handleChange = (file: File) => {
    setUrl(URL.createObjectURL(file));
    onChange && onChange(file as File);
  };

  const handleFileChange = (files: File[]) => {
    if (multiple && onMultipleChange) {
      return onMultipleChange(files);
    }

    const file = files[0];
    handleChange(file);
  };

  const openFileNavigator = async () => {
    const files = await selectFiles({
      multiple,
      accept,
    });

    if (!files || files.length === 0) {
      return;
    }

    handleFileChange(files);
  };

  const { handleDrop } = useFileDropzone({
    handler: handleChange,
  });

  return (
    <UploadContainer onDrop={handleDrop} id="photo-uploader" onClick={openFileNavigator}>
      <Placeholder>
        <Icon>
          <Plus />
        </Icon>

        {buttonName}

        <span>
          {placeholderTitle} <br />
          {placeholderDescription}
        </span>
      </Placeholder>
    </UploadContainer>
  );
};

const Placeholder = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(54, 123, 255, 0.12);
  color: var(--color-secondary);
  font-size: 1.6rem;
  padding: 0 2rem;

  span {
    display: inline-block;
    margin-top: 0.8rem;
    font-size: 1.4rem;
    line-height: 1.8rem;
    color: rgba(255, 255, 255, 0.4);
    text-align: center;
  }

  &:hover {
    cursor: pointer;
  }
`;

const Icon = styled.div`
  height: 3.2rem;
  width: 3.2rem;
  border-radius: var(--border-radius-medium);
  cursor: pointer;
  display: flex;
  font-size: 2.4rem;
  align-items: center;
  justify-content: center;
  margin-right: 0.8rem;
  background-color: var(--color-secondary);
  color: #273046;
  margin-bottom: 1.2rem;
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: var(--border-radius-medium);
  border: 1.5px dashed var(--color-secondary);
`;
